/**
 * The direction-aware record block.
 *
 * It has one consumer today and it stays in the kit anyway, because it is a
 * rule-enforcer rather than a feature. The kit-wide ban on physical
 * `left` / `right` / `marginLeft` / `textAlign: left` makes it cheap, and the
 * defect it prevents is not re-derivable by a pillar author who reaches for
 * `dir="auto"` on leaf nodes: per-node auto-direction lets one record's title
 * resolve right-to-left while its own metadata resolves left-to-right, and the
 * block ends up with two start edges.
 *
 * One record, one direction, one start edge — set on the block ROOT, resolved
 * through a closed map, and never guessed per node.
 */
"use client";
import type { ReactElement, ReactNode } from "react";

/** Language subtags whose script runs right to left. */
enum CresultcardRtlLanguage {
  Arabic = "ar",
  Hebrew = "he",
  Persian = "fa",
  Urdu = "ur",
  Yiddish = "yi",
  Divehi = "dv",
  Pashto = "ps",
  Sindhi = "sd",
  Uyghur = "ug",
}

enum CresultcardDirection {
  LeftToRight = "ltr",
  RightToLeft = "rtl",
}

const RTL_SUBTAGS = new Set<string>(Object.values(CresultcardRtlLanguage));
const SUBTAG_SEPARATOR = "-";

interface CresultcardBidiProps {
  readonly children: ReactNode;
  /** A BCP-47 tag from the record itself, e.g. `he-IL`. */
  readonly lang: string;
}

/** The primary subtag decides; a region or a script never flips a direction. */
function directionOf(lang: string): CresultcardDirection {
  const primary = lang.toLowerCase().split(SUBTAG_SEPARATOR)[0] ?? lang;
  return RTL_SUBTAGS.has(primary)
    ? CresultcardDirection.RightToLeft
    : CresultcardDirection.LeftToRight;
}

export function CresultcardBidi({
  children,
  lang,
}: CresultcardBidiProps): ReactElement {
  return (
    <div
      data-el="Cresultcard/Bidi/Root"
      dir={directionOf(lang)}
      lang={lang}
      style={{ display: "flex", flexDirection: "column", minWidth: 0 }}
    >
      {children}
    </div>
  );
}
